import { ArrowUpRightIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { GQLFlipTilesFragment, GQLPromoImageFragment } from '@theprintbar/graphql';
import { BrandColours, ImageSize, cn, sizedImage } from '@theprintbar/shared';
import { ButtonSize, OutlineLinkButton, PrimaryLinkButton, PromoImageContainer, RichText } from '@theprintbar/ui';
import React from 'react';

export interface FlipTileProps {
	className?: string;
	tile?: NonNullable<GQLFlipTilesFragment['flipTiles']['structure']>[0];
	image?: GQLPromoImageFragment | null;
	link?:
		| {
				to?: string | null | undefined;
				text?: string | null | undefined;
				isPrimary?: boolean | null | undefined;
		  }
		| null
		| undefined;
}

export function FlipTile({ className, tile, image, link }: FlipTileProps) {
	if (!tile) return null;

	const { title, titleColour, text, links: tileLinks, promoImage, icon } = tile;
	const [isHovered, setIsHovered] = React.useState(false);

	const handleMouseEnter = () => {
		setIsHovered(true);
	};

	const handleMouseLeave = () => {
		setIsHovered(false);
	};

	const links = link ? [link] : tileLinks;

	return (
		<div className={cn('relative w-full h-full', className)}>
			{/* Pill */}
			<div
				className={cn(
					'z-20 absolute top-0 -translate-y-1/2 left-4 border border-blue rounded-full py-[5px] px-[15px] flex justify-center items-center max-w-96',
				)}
				style={{ backgroundColor: titleColour || BrandColours.BrightBlue }}
			>
				<h4 className="uppercase text-base lg:text-lg text-center">{title}</h4>
			</div>

			<div
				className={cn('relative group overflow-hidden rounded-3xl border border-blue min-h-[267px] h-full')}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				<div
					className={cn(
						'z-10 opacity-100 transition-all duration-500 ease-in-out absolute inset-0 overflow-hidden pointer-events-none',
						isHovered ? 'group-hover:opacity-0' : 'group-hover:opacity-100',
					)}
				>
					<PromoImageContainer promoImage={promoImage || image} className="w-full h-full" promoPosition="none" />

					{icon && (
						<div className="absolute left-4 bottom-4 rounded-full overflow-hidden">
							<img
								loading="lazy"
								src={sizedImage(icon.image.url, ImageSize.Tiny)}
								alt={icon?.altText || 'icon'}
								className="w-12 h-12 bg-blue"
							/>
						</div>
					)}

					<InformationCircleIcon className="absolute right-4 bottom-4 lg:hidden w-6 h-6 fill-white" />
				</div>

				<div className="bg-blue-bright/20 overflow-hidden p-7 flex flex-col h-full relative">
					<RichText className="space-y-2" content={text?.content} />
					{!!links?.length && (
						<div className={cn('flex sm:flex-col sm:absolute sm:right-8 sm:bottom-8 sm:gap-2.5 justify-between pt-2 mt-auto')}>
							{links.map((tileLink, index) =>
								tileLink?.isPrimary ? (
									<PrimaryLinkButton
										key={index}
										to={tileLink.to || ''}
										icon={ArrowUpRightIcon}
										iconSize={ButtonSize.S}
										className="font-belfast font-bold text-xs w-max uppercase"
									>
										{tileLink?.text || 'Read More'}
									</PrimaryLinkButton>
								) : tileLink ? (
									<OutlineLinkButton
										key={index}
										to={tileLink?.to || ''}
										icon={ArrowUpRightIcon}
										iconSize={ButtonSize.S}
										className="font-belfast font-bold text-xs w-max uppercase"
									>
										{tileLink?.text || 'Read More'}
									</OutlineLinkButton>
								) : null,
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
